import React, { useCallback, useEffect, useState } from 'react';
import {
  getEpisodesByCreator,
  getSimilarEpisodes,
  setVote,
  getMyVote,
} from '../../lib/episodeSocial';
import { useAuth } from '../AuthContext';
import { storageImage } from '../../lib/img';
import Spinner from '../shared/Spinner';

/**
 * Discovery panel under the episode body. Two tabs:
 *   • Similar      — fuzzy match by niche / format
 *   • By [Creator] — other episodes from the same creator
 *
 * Each row carries its own engagement actions — Spin-off (plain, not
 * the pink hero CTA), Like (heart), and a ⋯ overflow for follow-ups.
 */
export default function EpisodeRelatedPanel({
  episode,
  show,
  shows = [],
  creatorName = 'this creator',
  onSelectEpisode,
  onSpinoff,
}) {
  const [tab, setTab]         = useState('similar');
  const [similar, setSimilar] = useState([]);
  const [byCreator, setByCreator] = useState([]);
  const [loading, setLoading] = useState(false);

  const showById = React.useMemo(() => {
    const m = {};
    for (const s of shows) m[s.id] = s;
    if (show) m[show.id] = show;
    return m;
  }, [show, shows]);

  const reload = useCallback(async () => {
    if (!episode?.id) return;
    setLoading(true);
    try {
      const [s, bc] = await Promise.all([
        getSimilarEpisodes(episode, 20),
        episode.user_id ? getEpisodesByCreator(episode.user_id, episode.id, 20) : Promise.resolve([]),
      ]);
      setSimilar(s);
      setByCreator(bc);
    } catch (err) {
      console.error('related load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [episode?.id, episode?.user_id, episode?.niche, episode?.format]); // eslint-disable-line

  useEffect(() => { reload(); }, [reload]);

  const rows = tab === 'similar' ? similar : byCreator;

  return (
    <div className="ws-related">
      <div className="ws-related-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'similar'}
          className={`ws-related-tab${tab === 'similar' ? ' active' : ''}`}
          onClick={() => setTab('similar')}
        >
          Similar
        </button>
        <button
          role="tab"
          aria-selected={tab === 'by-creator'}
          className={`ws-related-tab${tab === 'by-creator' ? ' active' : ''}`}
          onClick={() => setTab('by-creator')}
          title={`More from ${creatorName}`}
        >
          By {creatorName.length > 14 ? `${creatorName.slice(0, 12)}…` : creatorName}
        </button>
      </div>

      <div className="ws-related-list">
        {loading ? (
          <div className="ws-related-empty"><Spinner center size={20} /></div>
        ) : rows.length === 0 ? (
          <div className="ws-related-empty">
            {tab === 'similar'    && 'Nothing similar yet.'}
            {tab === 'by-creator' && 'No other episodes from this creator.'}
          </div>
        ) : (
          rows.map(ep => (
            <RelatedRow
              key={ep.id}
              ep={ep}
              show={showById[ep.show_id]}
              onSelect={() => onSelectEpisode?.(ep)}
              onSpinoff={onSpinoff ? () => onSpinoff(ep) : undefined}
            />
          ))
        )}
      </div>
    </div>
  );
}

function RelatedRow({ ep, show, onSelect, onSpinoff }) {
  const { user } = useAuth();
  const title = ep?.result?.title || ep?.topic || 'Untitled';
  const accent = show?.brand_color || '#c6364a';

  const [vote, setVoteState] = useState(0);
  const [likeCount, setLikeCount] = useState(ep?.like_count || 0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!user?.id || !ep?.id) return;
    (async () => {
      const v = await getMyVote(user.id, ep.id);
      if (alive) setVoteState(v);
    })();
    return () => { alive = false; };
  }, [user?.id, ep?.id]);

  async function castVote(e) {
    e.stopPropagation();
    if (!user?.id || !ep?.id) return;
    const next = vote === 1 ? 0 : 1;
    setLikeCount(c => c + (next === 1 ? 1 : -1));
    setVoteState(next);
    try { await setVote(user.id, ep.id, next); }
    catch { setVoteState(vote); setLikeCount(ep?.like_count || 0); }
  }

  function handleSpinoff(e) {
    e.stopPropagation();
    onSpinoff?.();
  }
  function toggleMenu(e) {
    e.stopPropagation();
    setMenuOpen(v => !v);
  }
  function copyLink(e) {
    e.stopPropagation();
    setMenuOpen(false);
    const slug = ep?.slug || ep?.result?.slug;
    if (!slug) return;
    try { navigator.clipboard.writeText(`${window.location.origin}/e/${slug}`); } catch {}
  }

  return (
    <div className="ws-related-row">
      <button className="ws-related-row-main" onClick={onSelect} type="button">
        <div
          className="ws-related-thumb"
          style={show?.cover_art_url
            ? { backgroundImage: `url(${storageImage(show.cover_art_url, { width: 200 })})` }
            : { background: `linear-gradient(155deg, ${accent} 0%, #0c0808 85%)` }}
        />
        <div className="ws-related-meta">
          <div className="ws-related-title" title={title}>{title}</div>
          <div className="ws-related-sub">
            {ep?.niche || ep?.format || 'episode'}
          </div>
        </div>
      </button>
      <div className="ws-related-actions">
        {onSpinoff && (
          <button
            type="button"
            className="ws-related-action"
            onClick={handleSpinoff}
            title="Build a spin-off from this episode"
            aria-label="Spin-off"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="16 3 21 3 21 8"/>
              <line x1="4" y1="20" x2="21" y2="3"/>
              <polyline points="21 16 21 21 16 21"/>
              <line x1="15" y1="15" x2="21" y2="21"/>
              <line x1="4" y1="4" x2="9" y2="9"/>
            </svg>
          </button>
        )}
        <button
          type="button"
          className={`ws-related-action${vote === 1 ? ' is-active' : ''}`}
          onClick={castVote}
          title={vote === 1 ? 'Unlike' : 'Like'}
          aria-pressed={vote === 1}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill={vote === 1 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          {likeCount > 0 && <span className="ws-related-action-count">{likeCount}</span>}
        </button>
        <div className="ws-related-more-wrap">
          <button
            type="button"
            className="ws-related-action"
            onClick={toggleMenu}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title="More"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <circle cx="5" cy="12" r="1.5"/>
              <circle cx="12" cy="12" r="1.5"/>
              <circle cx="19" cy="12" r="1.5"/>
            </svg>
          </button>
          {menuOpen && (
            <>
              <button type="button" className="ws-related-more-backdrop" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }} aria-label="Close" />
              <div className="ws-related-more-menu" role="menu">
                <button type="button" role="menuitem" onClick={copyLink}>Copy link</button>
                <button type="button" role="menuitem" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onSelect(); }}>Open</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
